import { sortBy } from 'lodash';
import { type IMapGroup } from '../MapGroup.ts';

/** Risultato del raggruppamento dei layer */
export interface IGroupedLayers {
  /** Gruppi ordinati per campo `order` */
  sortedGroups: IMapGroup[];
  /** Mappa nome gruppo → indici dei layer appartenenti */
  groupedIndices: Record<string, number[]>;
  /** Indici dei layer senza gruppo o con riferimento a un gruppo inesistente */
  ungroupedIndices: number[];
  /**
   * `zOrder[i]` = z-index OpenLayers per il layer all'indice `i`.
   * Top della legenda = z più alto sulla mappa.
   */
  zOrder: number[];
}

/**
 * Raggruppa gli indici dei layer per riferimento al gruppo e ordina i gruppi per `order`.
 * I layer che referenziano un gruppo inesistente vengono trattati come non raggruppati.
 */
export function buildGroupedLayers(
  layers: { group?: string | null }[],
  groups: IMapGroup[],
): IGroupedLayers {
  // Mappa nome gruppo → lista di indici dei layer appartenenti (Object.create(null) evita collisioni con proprietà di Object.prototype)
  const groupedIndices: Record<string, number[]> = Object.create(null);
  // Indici dei layer che non appartengono a nessun gruppo valido
  const ungroupedIndices: number[] = [];
  // Set per lookup O(1) invece di groups.some() O(m) ad ogni layer
  const groupNameSet = new Set(groups.map(group => group.name));

  layers.forEach((layer, i) => {
    // Il layer ha un gruppo valido solo se:
    // 1. il campo `group` è valorizzato (non null/undefined/stringa vuota)
    // 2. esiste un gruppo con quel nome nella lista `groups`
    // In caso contrario (campo assente, null, o riferimento orfano a gruppo cancellato)
    // il layer viene trattato come non raggruppato.
    if (layer.group && groupNameSet.has(layer.group)) {
      // Inizializza l'array per il gruppo se è il primo layer che lo referenzia
      if (!groupedIndices[layer.group]) {
        groupedIndices[layer.group] = [];
      }
      // L'indice preserva l'ordine originale dell'array layers (z-order della mappa)
      groupedIndices[layer.group].push(i);
    } else {
      ungroupedIndices.push(i);
    }
  });

  // Ordina i gruppi per il campo `order`.
  // I gruppi senza `order` vanno in fondo (MAX_SAFE_INTEGER come fallback).
  const sortedGroups = sortBy(
    groups,
    group => group.order ?? Number.MAX_SAFE_INTEGER,
  );

  // Display order (top legenda → bottom): gruppi ordinati per `order`, poi ungrouped.
  const displayOrder: number[] = [];
  for (const group of sortedGroups) {
    const indices = groupedIndices[group.name];
    if (indices) {
      for (const idx of indices) {
        displayOrder.push(idx);
      }
    }
  }
  for (const idx of ungroupedIndices) {
    displayOrder.push(idx);
  }

  // Reverse del display order → z-index (top legenda = z più alto sulla mappa)
  const zOrder = new Array<number>(displayOrder.length).fill(0);
  [...displayOrder].reverse().forEach((layerIdx, z) => {
    zOrder[layerIdx] = z;
  });

  return {
    sortedGroups,
    groupedIndices,
    ungroupedIndices,
    zOrder,
  };
}
